import type { LandingContent } from "./types";
import { rasterSrc } from "@/generated/raster-images";

const heroSolar = rasterSrc("solar-pv/hero-solar");
const solarAssessmentRoof = rasterSrc("solar-pv/solar-assessment-roof");
const whyGenerateImg = rasterSrc("solar-pv/why-generate");
const whyBatteryImg = rasterSrc("solar-pv/why-battery");
const whyExportImg = rasterSrc("solar-pv/why-export");
const whyDesignedImg = rasterSrc("solar-pv/why-designed");

/**
 * Body content for the /solar-pv (residential) page. Drives the shared
 * LpOrderedSections renderer (trust, testimonials, pricing, FAQ).
 *
 * Compliance:
 * - Finance / APR / representative example / eligibility wording is
 * - Generation / kWh figures are MCS performance estimates with
 * - No generic '£X per year' savings wording. No 'independent' /
 *   'no commission' framing.
 */
export const solarPvLp: LandingContent = {
  slug: "solar-pv",
  path: "/lp/solar-pv",
  title: "Solar PV For Your Home In South Wales",
  metaTitle: "Solar PV for Your Home South Wales | MCS Certified | CRS Energy",
  metaDescription:
    "MCS certified, DNO compliant solar PV design and install across South Wales. Optional battery storage, SEG export, manufacturer-backed warranties. Free solar estimate.",
  defaultService: "Solar PV",
  heroHeadline: "Lower Your Energy Bills With Expert Solar Installation",
  heroSubheading:
    "Expertly designed, MCS-certified solar PV systems for homes across South Wales and the South West of England. Talk to our team, get a personalised estimate on your first call, and choose how you'd like to pay - with optional battery storage and finance options available.",
  heroBullets: [
    "Installed by accredited solar specialists",
    "We take care of all approvals and paperwork",
    "Manufacturer-backed product warranties",
    "Optional battery storage to maximise your savings",
    "Finance options available, subject to status",
    "Personalised estimate from your first call - no home visit needed",
  ],
  heroImage: heroSolar,
  heroAlt: "Black monocrystalline solar PV panels installed on the pitched roof of a UK home",
  heroFloatImages: [
    { src: heroSolar, slug: "solar-hero" },
    { src: solarAssessmentRoof, slug: "solar-assessment" },
    { src: whyGenerateImg, slug: "solar-generate" },
    { src: whyBatteryImg, slug: "solar-battery" },
    { src: whyExportImg, slug: "solar-export" },
    { src: whyDesignedImg, slug: "solar-designed" },
  ],
  trustItems: [
    "MCS Certified",
    "DNO / G99 Compliant",
    "Manufacturer-Backed Warranties",
    "Finance Available",
    "Free Solar Estimate",
  ],
  problemTitle: "Why It Matters",
  problemPoints: [
    {
      title: "Generate Your Own Electricity",
      body: "A roof-mounted PV array generates electricity directly from daylight, reducing what you draw from the grid during the day. MCS performance estimate provided in your written proposal.",
    },
    {
      title: "Daytime Use + Battery Self-Consumption",
      body: "Pair the system with a battery and more of what you generate is used in your home, including in the evening, rather than exported. Sizing is matched to your usage pattern.",
    },
    {
      title: "Export Via SEG",
      body: "Surplus generation can be exported to the grid through the Smart Export Guarantee. We register the MCS certificate so you can apply for an SEG tariff with your chosen supplier.",
    },
  ],
  clarityTitle: "What A Properly Designed Solar System Looks Like",
  clarityBody:
"An array sized and laid out for your specific roof, with shading and orientation considered, the right inverter for the array, an optional battery sized to your usage, a DNO/G99 application submitted on your behalf, full commissioning records, and MCS certification on completion.",
  productTitle: "What We Install",
  productPoints: [
    {
      title: "MCS-Certified Solar PV Array",
      body: "Manufacturer-supplied panels laid out for your specific roof, with appropriate mounting and weathering.",
    },
    {
      title: "Inverter",
      body: "String or hybrid inverter specified to the array, with monitoring set up at handover.",
    },
    {
      title: "Optional Battery Storage",
      body: "Battery sized to your usage pattern, paired with the inverter for self-consumption. Always presented as an option, not pushed.",
    },
    {
      title: "DNO / G99 & Electrical Works",
      body: "Consumer-unit work where required, isolation and metering, plus the DNO/G99 application handled by us.",
    },
  ],
  processSteps: [
    {
      step: 1,
      title: "Estimate & Design",
      description:
        "Personalised estimate on your first call from roof data, usage and what you want from solar. Detailed desktop survey confirms the final design after you go ahead.",
      icon: "magnifier",
    },
    {
      step: 2,
      title: "Proposal & DNO",
      description:
        "Written, property-specific proposal with MCS performance estimate. We prepare and submit the DNO / G99 application on your behalf.",
      icon: "file",
    },
    {
      step: 3,
      title: "Install & Commission",
      description:
        "MCS-certified install of panels, inverter and optional battery. Full electrical, commissioning and monitoring setup.",
      icon: "drill",
    },
    {
      step: 4,
      title: "Registration & Aftercare",
      description:
        "MCS certificate issued for SEG, manufacturer warranties registered and monitoring demoed on handover.",
      icon: "certificate",
    },
  ],
  qualificationTitle: "What We Assess Before Quoting",
  qualificationItems: [
"Roof orientation, pitch and shading",
"Roof condition and structure",
"Consumer unit and incoming electrics",
"DNO / G99 application requirements",
"Battery suitability and siting",
"Monitoring setup and connectivity",
  ],
  includedItems: [
    "Free solar estimate",
    "Property-specific design and layout",
    "MCS-certified install",
    "DNO / G99 application handled for you",
    "Inverter and optional battery",
    "Commissioning records",
    "MCS certificate and manufacturer warranty registration",
  ],
  trustSectionTitle: "A Proper Estimate Means A Better System",
  trustSectionBody:
    "We don't generate instant prices from a postcode alone. Your personalised estimate on the first call is based on your roof, your usage and what you want from solar - with the detailed desktop survey confirming the final design once you go ahead.",
  benefits: [
    { title: "Designed Array", description: "Layout matched to your roof, orientation and shading." },
    { title: "DNO / G99 Handled", description: "We prepare and submit the network operator application for you." },
    { title: "Optional Battery", description: "Battery sized to your usage, never pushed as default." },
    { title: "MCS Certificate", description: "Issued on completion so you can apply for SEG export." },
  ],
  testimonialsFull: [
    {
      name: "Frank Ciccotti",
      source: "trustpilot",
      date: "July 2026",
      serviceTag: "Solar PV, Heat Pump & Insulation",
      quote:
        "Highly competent work, from initial design to installation. We had an air source heat pump, solar panels and extensive insulation installed. Large teams of skilled tradespeople arrived in planned succession. Work was completed within agreed timescales. Problems, where they arose, were quickly resolved. They even painted after completing the work!",
    },
    {
      name: "Ken",
      source: "google",
      date: "May 2024",
      serviceTag: "Heating Upgrade",
      quote:
        "I contacted my local MP Elwyn Vaughn who gave me the name of a company to contact, who then put me in touch with Complete Remedial Solutions in Bridgend. This company got the system up and running today Wednesday 29/05/2024. The manager Rui and the electricians Mike and Mathew who I would recommend to anyone needing a heating upgrade installation, have been absolutely first class in their care and work. I can't thank you enough guys. If I could give more than 5 stars I would.",
    },
    {
      name: "Polly",
      source: "trustpilot",
      date: "September 2023",
      serviceTag: "Solar PV & Air Source Heat Pump",
      quote:
        "Very helpful, friendly and reliable. I used CRS in spring 2023 to do a fairly big retrofit including installing an Air Source Heat Pump, completely new 'wet' central heating system and a small solar panel array. Keith and the team were really helpful and friendly throughout and although there were a few hiccups and delays for various reasons, they kept communicating with me which really helped, until it was all completed. I wouldn't hesitate to use them again.",
    },
  ],
  faqs: [
    {
      question: "How much electricity could a solar system generate for my home?",
      answer:
"Generation depends on your roof orientation, pitch, shading and array size. We provide an MCS performance estimate as part of your written proposal. We don't quote generic 'savings per year' figures because real-world savings depend on your usage pattern and tariff.",
    },
    {
      question: "Do I need a battery?",
      answer:
"No, solar PV works without a battery. A battery lets you use more of what you generate in your own home, including in the evening, rather than exporting it. We present it as an option and size it to your usage, never push it as default.",
    },
    {
      question: "What happens on cloudy days, or in winter?",
      answer:
"Panels still generate in diffuse light, just at lower output. Generation is highest on bright summer days and lowest in midwinter. Your MCS performance estimate accounts for typical UK conditions across the year.",
    },
    {
      question: "What warranties do I get?",
      answer:
"Panels, inverter and battery each carry manufacturer warranties that vary by product. We register them on your behalf and confirm the specifics in your written proposal. Workmanship warranty is also included.",
    },
    {
      question: "Can I get finance for solar?",
      answer:
        "Yes. Finance options are available for solar and battery installations, provided through Ideal4Finance and subject to status. One thing we're always clear about: monthly repayments are due every month for the length of your agreement, regardless of the savings your system achieves, and any savings estimates are not guaranteed. For independent estimates of typical solar savings, the Energy Saving Trust is a good place to start.",
    },
    {
      question: "What is SEG, and how does export work?",
      answer:
"The Smart Export Guarantee (SEG) lets you sell surplus electricity to a licensed energy supplier. You apply for an SEG tariff with your chosen supplier using your MCS certificate. Tariffs and terms are set by the supplier, not by us.",
    },
    {
      question: "Will someone need to visit my home before I get a price?",
      answer:
        "No. You'll get a personalised estimate on your first call, based on your property details, your electricity usage and aerial roof data. A detailed desktop survey completes your final design after you decide to go ahead - and we only arrange a home visit if something genuinely needs a closer look.",
    },
    {
      question: "How accurate is the estimate?",
      answer:
        "It's a genuine figure based on your roof, your usage and what you need - not a generic price. It's indicative, though: your final price and system design are confirmed by the detailed desktop survey, and if anything changes, we'll always talk it through with you first.",
    },
    {
      question: "What is a desktop survey?",
      answer:
        "A full system design completed remotely by our team, using aerial mapping, measured roof dimensions, orientation and shading analysis, your electricity usage and photographs of your property. It covers everything an in-person survey would for most homes - and where a property does need a physical inspection, we arrange one before installation.",
    },
    {
      question: "What happens after I pay or my finance is approved?",
      answer:
        "We complete your detailed desktop survey and system design, confirm everything with you in writing, and then book your installation. If the design flags anything that needs a physical check, we arrange that first.",
    },
    {
      question: "What if my home isn't suitable for solar?",
      answer:
        "We check suitability before you commit, not after. Your estimate is based on an initial desktop check of your property - your roof, its orientation and any shading - so if solar isn't a good fit, we'll tell you at the estimate stage, before you've paid anything or applied for finance. The detailed desktop survey after you go ahead is about finalising your design, not re-deciding whether solar works for your home.",
    },
    {
      question: "How much does the fixed-price solar package cost?",
      answer:
        "£5,999 for 6 panels, a hybrid inverter and a 7.2kWh stackable battery, fully installed by our MCS-accredited team, with scaffolding, bird proofing, MCS certification and DNO notification included. Subject to suitability, confirmed by your detailed desktop survey.",
    },
    {
      question: "Which areas do you cover for solar installation?",
      answer:
        "We install solar PV across South Wales and the South West of England from our base in Bridgend. Our team is MCS certified (Certificate No. IAA-10295), a HIES consumer code member and TrustMark registered.",
    },
    {
      question: "Are there grants for solar panels in Wales?",
      answer:
        "There is no universal solar grant in Wales, but targeted support exists: free installations through Warm Homes Nest and ECO4 for eligible households, interest-free loans through Green Homes Wales, 0% VAT until 31 March 2027 and Smart Export Guarantee payments. See our full guide to solar panel grants and funding in Wales.",
    },
  ],
  ctaTitle: "Get Your Free Solar Estimate",
  sectionOrder: ["testimonials", "faq"],
};

/**
 * Ad-group specific hero copy for /solar-pv?hero=<key>.
 * Unknown/absent keys must fall back to solarPvLp.heroHeadline/heroSubheading.
 * Only H1 and subheading text change - no layout, imagery, or CTA differences.
 */
export const SOLAR_HERO_VARIANTS = {
  installers: {
    headline: "MCS Certified Solar Installers for South Wales & the South West of England",
    subheading:
      "Trusted local solar PV installation from a fully accredited team - MCS, HIES and TrustMark registered. Get an indicative estimate on your first call, with no home visit before you're ready.",
  },
  cost: {
    headline: "Know What Solar Costs Before Anyone Visits Your Home",
    subheading:
      "Get a clear, indicative price on your very first call - no obligation, no hidden extras. Your price is agreed before any work begins.",
  },
  battery: {
    headline: "Store Your Solar Power, Use It When You Need It",
    subheading:
      "Solar PV with battery storage lets you use your daytime power in the evening and cut your reliance on the grid. MCS certified solar and battery installation across South Wales and the South West of England.",
  },
  savings: {
    headline: "How Much Could Solar Really Save You?",
    subheading:
      "Honest, realistic savings and payback estimates based on your home and how you use energy - plus payments for the electricity you export through the Smart Export Guarantee. Find out what solar could do for your bills on your first call.",
  },
} as const;

export type SolarHeroVariant = keyof typeof SOLAR_HERO_VARIANTS;

export const SOLAR_HERO_VARIANT_KEYS = Object.keys(SOLAR_HERO_VARIANTS) as SolarHeroVariant[];
